import { useState, type ReactNode } from 'react';

import { Dialog } from 'primereact/dialog';

import { useTranslation } from '../../../../shared/language';

import biometricQrApp from '../../../../assets/declaration-guidance/biometric-qr-app.png';
import biometricQrGoogle from '../../../../assets/declaration-guidance/biometric-qr-google.png';
import biometricStoreBadge from '../../../../assets/declaration-guidance/biometric-store-badge.png';
import closeIcon from '../../../../assets/declaration-guidance/direct-close.svg';
import otpQrApp from '../../../../assets/declaration-guidance/otp-qr-app.png';
import otpQrGoogle from '../../../../assets/declaration-guidance/otp-qr-google.png';
import otpStoreBadge from '../../../../assets/declaration-guidance/otp-store-badge.png';
import sectionClockIcon from '../../../../assets/declaration-guidance/section-clock.svg';
import sectionDocumentsIcon from '../../../../assets/declaration-guidance/section-documents.svg';
import sectionMethodIcon from '../../../../assets/declaration-guidance/section-method.svg';
import sectionUserIcon from '../../../../assets/declaration-guidance/section-user.svg';

export type DeclarationGuidanceType = 'AUTH_DIRECT' | 'AUTH_BIOMETRIC' | 'AUTH_OTP' | 'EXPLANATION';

export interface DeclarationGuidance {
  type: DeclarationGuidanceType;
  title?: string;
  subject?: string | string[];
  deadlineText?: string;
  documents?: string[];
  steps?: string[];
  stepsText?: string;
  note?: string;
  guideUrl?: string;
  appStoreUrl?: string;
  googlePlayUrl?: string;
  appStoreQrCodeUrl?: string;
  googlePlayQrCodeUrl?: string;
}

interface DeclarationGuidanceDialogProps {
  guidance: DeclarationGuidance | null;
  visible: boolean;
  onHide: () => void;
}

const sectionIcons = {
  user: sectionUserIcon,
  clock: sectionClockIcon,
  documents: sectionMethodIcon,
  method: sectionDocumentsIcon,
};

const mobileGuideAssets = {
  AUTH_BIOMETRIC: {
    appStoreQr: biometricQrApp,
    googlePlayQr: biometricQrGoogle,
    storeBadge: biometricStoreBadge,
  },
  AUTH_OTP: {
    appStoreQr: otpQrApp,
    googlePlayQr: otpQrGoogle,
    storeBadge: otpStoreBadge,
  },
};

interface GuidanceSectionProps {
  icon: keyof typeof sectionIcons;
  title: string;
  children: ReactNode;
}

const GuidanceSection = ({ icon, title, children }: GuidanceSectionProps) => (
  <section className="guidance-section">
    <div className="guidance-section-heading">
      <img className={`guidance-section-icon guidance-section-icon-${icon}`} src={sectionIcons[icon]} alt="" aria-hidden="true" />
      <h3 className="guidance-section-title">{title}</h3>
    </div>
    <div className="guidance-section-body">{children}</div>
  </section>
);

const GuidanceList = ({ items }: { items: string[] }) => (
  <ul className="guidance-list">
    {items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}
  </ul>
);

const QrDownload = ({
  imageUrl,
  storeUrl,
  storeName,
  badgeUrl,
}: {
  imageUrl?: string;
  storeUrl?: string;
  storeName: string;
  badgeUrl: string;
}) => {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(!imageUrl);
  const isAppStore = storeName === 'App Store';

  return (
    <div className="guidance-qr-card">
      {imageFailed ? (
        <div className="guidance-qr-fallback">
          <i className="pi pi-image" aria-hidden="true" />
          <span>{t('declarationGuidance.qrUnavailable')}</span>
        </div>
      ) : (
        <img
          className="guidance-qr-image"
          src={imageUrl}
          alt={t('declarationGuidance.qrAlt', { storeName })}
          onError={() => setImageFailed(true)}
        />
      )}
      {storeUrl && (
        <a
          className="guidance-store-badge"
          href={storeUrl}
          target="_blank"
          rel="noreferrer"
          aria-label={t('declarationGuidance.openStore', { storeName })}
        >
          <span className={`guidance-store-badge-image guidance-store-badge-image-${isAppStore ? 'apple' : 'google'}`}>
            <img src={badgeUrl} alt={`${storeName} badge`} />
          </span>
        </a>
      )}
    </div>
  );
};

const renderTextOrList = (value?: string | string[]) => {
  if (Array.isArray(value)) return <GuidanceList items={value} />;
  return value ? <p>{value}</p> : null;
};

const GuidanceNote = ({ children }: { children: ReactNode }) => (
  <div className="guidance-dialog-note">
    <div className="guidance-dialog-note-inner">{children}</div>
  </div>
);

export const DeclarationGuidanceDialog = ({ guidance, visible, onHide }: DeclarationGuidanceDialogProps) => {
  const { t } = useTranslation();
  if (!guidance) return null;

  const type = guidance.type;
  const isMobileGuide = type === 'AUTH_BIOMETRIC' || type === 'AUTH_OTP';
  const mobileAssets = type && mobileGuideAssets[type as keyof typeof mobileGuideAssets];

  return (
    <Dialog
      visible={visible}
      modal
      dismissableMask
      // PrimeReact chỉ xử lý closeOnEscape khi dialog ở trạng thái closable.
      // Nút đóng mặc định vẫn được ẩn bởi CSS để giữ nút đóng tùy biến bên dưới.
      closable
      closeOnEscape
      draggable={false}
      resizable={false}
      className="declaration-guidance-dialog"
      style={{
        width: '768px',
        maxWidth: 'calc(100vw - 40px)',
      }}
      breakpoints={{ '1200px': '82vw', '768px': '92vw', '640px': '96vw' }}
      onHide={onHide}
    >
      <div className="guidance-dialog-content">
        <header className="guidance-modal-header">
          <h2 className="guidance-dialog-title">{guidance.title || t('declarationGuidance.fallbackTitle')}</h2>
          <button type="button" className="guidance-dialog-close" aria-label={t('declarationGuidance.close')} onClick={onHide}>
            <img src={closeIcon} alt="" aria-hidden="true" />
          </button>
        </header>

        <div className="guidance-modal-body">
          <GuidanceSection icon="user" title={t('declarationGuidance.subject')}>
            {renderTextOrList(guidance.subject)}
          </GuidanceSection>

          <GuidanceSection icon="clock" title={t('declarationGuidance.deadline')}>
            <p className="guidance-deadline">{guidance.deadlineText}</p>
          </GuidanceSection>

          {guidance.documents?.length ? (
            <GuidanceSection icon="documents" title={t('declarationGuidance.documents')}>
              <GuidanceList items={guidance.documents} />
            </GuidanceSection>
          ) : null}

          <GuidanceSection icon="method" title={t('declarationGuidance.method')}>
            {isMobileGuide ? (
              <p>
                {guidance.stepsText}{' '}
                {guidance.guideUrl && (
                  <a className="guidance-inline-link" href={guidance.guideUrl} target="_blank" rel="noreferrer">
                    {t('declarationGuidance.openGuide')}
                  </a>
                )}
              </p>
            ) : (
              <>
                {guidance.stepsText ? <p>{guidance.stepsText}</p> : null}
                {!guidance.stepsText && guidance.steps?.length ? <GuidanceList items={guidance.steps} /> : null}
              </>
            )}
          </GuidanceSection>

          {guidance.note && <GuidanceNote>{guidance.note}</GuidanceNote>}

          {isMobileGuide && mobileAssets && (
            <div className="guidance-qr-section">
              <div className="guidance-qr-grid">
                <QrDownload
                  imageUrl={guidance.appStoreQrCodeUrl || mobileAssets.appStoreQr}
                  storeUrl={guidance.appStoreUrl}
                  storeName="App Store"
                  badgeUrl={mobileAssets.storeBadge}
                />
                <QrDownload
                  imageUrl={guidance.googlePlayQrCodeUrl || mobileAssets.googlePlayQr}
                  storeUrl={guidance.googlePlayUrl}
                  storeName="Google Play"
                  badgeUrl={mobileAssets.storeBadge}
                />
              </div>
              <p className="guidance-qr-caption">{t('declarationGuidance.qrCaption')}</p>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};
